'use client';

import { Button, FileDropZone, GeneralHeader, toast } from '@crabs-ai/ds-react';
import { FolderOpen, CloudUpload, Movie } from '@crabs-ai/web-icons';
import { useTranslations } from 'next-intl';
import { useCallback, useRef, useState } from 'react';
import { PlayerRef } from '@remotion/player';
import { useGetSpaceMeApi } from '@/features/my-space/api/get-space-me';
import { useAssetUpload } from '@/shared/hooks/useAssetUpload';
import { postContentUrlsApi } from '@/shared/apis/content/post-content-urls';
import {
  base64ToBlob,
  getMimeTypeFromBase64,
} from '@/features/gts/lib/utils/file-helpers';
import { AssetPickerModal } from '@/shared/components/asset-picker';
import { GetFolderItemsContentResponseType } from '@/shared/apis/folder/get-folder-items';
import { usePropertyPanelContext } from '@/features/remotion-editor/editor/contexts/property-panel-context';
import {
  useCurrentStateAsRef,
  useWriteContext,
} from '@/features/remotion-editor/editor/utils/use-context';
import { useAssetDownloadProgress } from '@/features/remotion-editor/editor/contexts/asset-download-progress-context';
import { useProjectTypeContext } from '@/features/remotion-editor/editor/contexts/project-type-context';
import { addAsset } from '@/features/remotion-editor/editor/assets/add-asset-wkwk';
import { classifyUploadFailure } from '@/shared/utils/upload-error-classifier';
import { showUploadFailureToast } from '@/shared/utils/upload-failure-toast';
import { CLIP_TYPES } from '@/shared/apis/editor/clip-types';
import { DtoContentType, DtoExtensionReverse } from '@/shared/constants';
import { GtsGalleryVideoItem } from '@/features/gts/model/gts-types';
import AiVideoGenerationView from './AiVideoGenerationView';
import AiVideoGeneratedList from './AiVideoGeneratedList';
import { useCaptureCurrentFrame } from './hooks/useCaptureCurrentFrame';

type ViewType = 'list' | 'generate';

interface AiVideoContentProps {
  projectId: number;
  playerRef: React.RefObject<PlayerRef | null>;
  variant?: 'full' | 'generated-only' | 'generate-only';
}

export default function AiVideoContent({
  projectId,
  playerRef,
  variant = 'full',
}: AiVideoContentProps) {
  const t = useTranslations();
  const inputRef = useRef<HTMLInputElement>(null);
  const { setActiveMenu } = usePropertyPanelContext();
  const { data: spaceData } = useGetSpaceMeApi();
  const { captureFrame } = useCaptureCurrentFrame(playerRef);
  const { imageUpload } = useAssetUpload();
  const timelineWriteContext = useWriteContext();
  const stateAsRef = useCurrentStateAsRef();
  const { fetchAssetWithProgress } = useAssetDownloadProgress();
  const { projectType } = useProjectTypeContext();

  const [currentView, setCurrentView] = useState<ViewType>('list');
  const [selectedImageUrl, setSelectedImageUrl] = useState<string>('');
  const [previewImageUrl, setPreviewImageUrl] = useState<string>('');
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  // 지연 업로드: generate 버튼 클릭 시 호출되어 S3 업로드 후 CDN URL 반환
  const uploadPendingFile = useCallback(async (): Promise<string | null> => {
    if (!pendingFile) return null;
    const spaceId = spaceData?.data?.id;
    if (!spaceId) return null;

    let contentId = 0;
    await imageUpload({
      file: pendingFile,
      spaceId,
      project: { id: projectId, isAsset: false },
      onComplete: (result) => {
        contentId = result.id;
      },
    });

    if (!contentId) return null;

    const urlsResponse = await postContentUrlsApi([contentId]);
    return urlsResponse.data?.[String(contentId)] ?? null;
  }, [pendingFile, spaceData, projectId, imageUpload]);

  // 로컬 파일 선택 처리 (업로드는 generate 시 실행)
  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0] as File | undefined;
    if (!file || !file.type.startsWith('image/')) return;

    const localPreviewUrl = URL.createObjectURL(file);
    setPendingFile(file);
    setPreviewImageUrl(localPreviewUrl);
    setSelectedImageUrl('');
    setCurrentView('generate');
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      handleFileSelect(e.dataTransfer.files);
    },
    [handleFileSelect],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFileSelect(e.target.files);
      // 같은 파일을 다시 선택할 수 있도록 초기화
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [handleFileSelect],
  );

  // 프로젝트 소스에서 가져오기
  const handleImportFromProject = useCallback(() => {
    setActiveMenu('asset');
  }, [setActiveMenu]);

  // 내 스페이스에서 가져오기
  const handleImportFromSpace = useCallback(() => {
    setIsPickerOpen(true);
  }, []);

  // 내 스페이스에서 선택 완료 (이미 CDN URL이 있으므로 pendingFile 불필요)
  const handleAssetSelect = useCallback(
    (items: GetFolderItemsContentResponseType[]) => {
      if (items.length === 0) return;
      const item = items[0] as GetFolderItemsContentResponseType | undefined;
      if (!item) return;
      if (item.publicUrl) {
        setPendingFile(null);
        setSelectedImageUrl(item.publicUrl);
        setPreviewImageUrl(item.publicUrl);
        setCurrentView('generate');
      }
    },
    [],
  );

  // 현재 프레임 캡처 (업로드는 generate 시 실행)
  const handleCaptureCurrentFrame = useCallback(async () => {
    const dataUrl = await captureFrame();
    if (!dataUrl) {
      toast({
        type: 'error',
        description: '현재 프레임을 캡처할 수 없습니다.',
        toastOptions: { position: 'top-center' },
      });
      return;
    }
    const mimeType = getMimeTypeFromBase64(dataUrl);
    const blob = base64ToBlob(dataUrl, mimeType);
    const file = new File([blob], 'captured-frame.png', { type: mimeType });

    const localPreviewUrl = URL.createObjectURL(file);
    setPendingFile(file);
    setPreviewImageUrl(localPreviewUrl);
    setSelectedImageUrl('');
    setCurrentView('generate');
  }, [captureFrame]);

  // 뒤로가기 (generate → list)
  const handleBack = useCallback(() => {
    if (previewImageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewImageUrl);
    }
    setPendingFile(null);
    setCurrentView('list');
    setSelectedImageUrl('');
    setPreviewImageUrl('');
  }, [previewImageUrl]);

  // 생성 성공 핸들러
  const handleGenerateSuccess = useCallback(() => {
    if (previewImageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewImageUrl);
    }
    setPendingFile(null);
    setCurrentView('list');
    setSelectedImageUrl('');
    setPreviewImageUrl('');
  }, [previewImageUrl]);

  // 생성된 영상 클릭 핸들러 (타임라인에 추가)
  const handleVideoClick = useCallback(
    async (video: GtsGalleryVideoItem, startFrame?: number) => {
      try {
        if (!video.publicUrl) {
          throw new Error('publicUrl이 없습니다.');
        }

        const blob = await fetchAssetWithProgress(
          video.publicUrl,
          `AI Video #${video.id}`,
          video.extension,
        );

        // rerender-defer-reads: 콜백 실행 시점에만 state 읽기
        const state = stateAsRef.current;
        const tracks = state.undoableState.tracks;
        const fps = state.undoableState.fps;
        const compositionWidth = state.undoableState.compositionWidth;
        const compositionHeight = state.undoableState.compositionHeight;

        const contentId = video.contentId?.toString() ?? video.id;
        const extension =
          video.extension != null
            ? (DtoExtensionReverse[
                video.extension as keyof typeof DtoExtensionReverse
              ] ?? 'mp4')
            : 'mp4';

        const timelineStartFrame =
          startFrame ?? playerRef.current?.getCurrentFrame() ?? 0;

        await addAsset({
          file: blob,
          contentId,
          filename: `AI Video #${video.id}.${extension}`,
          clip: {
            clipType: CLIP_TYPES.VIDEO,
            contentPublicUri: video.publicUrl,
            videoStart: 0,
            videoEnd: video.duration ?? undefined,
          },
          timelineWriteContext,
          playerRef,
          dropPosition: null,
          fps,
          compositionWidth,
          compositionHeight,
          tracks,
          projectType: projectType!,
          isAsset: true,
          extension,
          timelineStartFrame,
        });

        toast({
          type: 'success',
          description: t('Editor.ai-video.add-to-timeline-success'),
          toastOptions: { position: 'top-center' },
        });
      } catch (error) {
        console.error(
          '[AiVideoContent] handleVideoClick addAsset failed:',
          error,
        );
        showUploadFailureToast(
          t,
          classifyUploadFailure(error, `AI Video #${video.id}`),
        );
      }
    },
    [
      fetchAssetWithProgress,
      stateAsRef,
      timelineWriteContext,
      playerRef,
      projectType,
      t,
    ],
  );

  // generate 뷰
  if (currentView === 'generate') {
    return (
      <div className="absolute flex h-full w-full flex-col">
        <GeneralHeader
          size="lg"
          responsive={false}
          title={t('Editor.ai-video.title')}
        />

        <div className="flex min-h-0 flex-1 flex-col px-md py-sm layout-padding-bottom">
          <AiVideoGenerationView
            imageUrl={selectedImageUrl}
            previewImageUrl={previewImageUrl}
            projectId={projectId}
            onCancel={handleBack}
            onGenerateSuccess={handleGenerateSuccess}
            uploadPendingFile={pendingFile ? uploadPendingFile : undefined}
          />
        </div>
      </div>
    );
  }

  // list 뷰 (기본)
  return (
    <div
      className={
        variant === 'generated-only'
          ? 'flex w-full flex-col'
          : 'absolute flex h-full w-full flex-col overflow-y-auto layout-padding-bottom'
      }
    >
      {(variant === 'full' || variant === 'generate-only') && (
        <>
          {variant === 'full' && (
            <GeneralHeader
              size="lg"
              responsive={false}
              title={t('Editor.ai-video.title')}
            />
          )}

          {/* 업로드 영역 */}
          <div className="flex w-full flex-col items-stretch gap-xs px-md py-sm">
            <input
              ref={inputRef}
              className="hidden"
              type="file"
              accept="image/*"
              onChange={handleFileInputChange}
            />
            <FileDropZone
              labelText={t('Editor.ai-video.file-drop-label')}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
            />

            {/* 프로젝트 소스에서 가져오기 */}
            <Button
              hierarchy="tertiary"
              size="small"
              onClick={handleImportFromProject}
            >
              <span className="flex items-center justify-center gap-5xs whitespace-nowrap">
                <FolderOpen className="size-sm [&_path]:fill-sys-cont-neutral-default" />
                <span className="text-comp-accent-small">
                  {t('Editor.ai-video.import-from-project')}
                </span>
              </span>
            </Button>

            {/* 내 스페이스에서 가져오기 */}
            <Button
              hierarchy="tertiary"
              size="small"
              onClick={handleImportFromSpace}
            >
              <span className="flex items-center justify-center gap-5xs whitespace-nowrap">
                <CloudUpload className="size-sm [&_path]:fill-sys-cont-neutral-default" />
                <span className="text-comp-accent-small">
                  {t('Editor.ai-video.import-from-space')}
                </span>
              </span>
            </Button>

            {/* 현재 프레임의 영상을 사용하기 */}
            <Button
              hierarchy="tertiary"
              size="small"
              onClick={handleCaptureCurrentFrame}
            >
              <span className="flex items-center justify-center gap-5xs whitespace-nowrap">
                <Movie className="size-sm [&_path]:fill-sys-cont-neutral-default" />
                <span className="text-comp-accent-small">
                  {t('Editor.ai-video.use-current-frame')}
                </span>
              </span>
            </Button>
          </div>

          {variant === 'full' && (
            <>
              {/* 생성한 영상 섹션 */}
              <GeneralHeader
                size="lg"
                responsive={false}
                title={t('Editor.ai-video.generated-videos')}
              />
            </>
          )}
        </>
      )}

      {variant !== 'generate-only' && (
        <AiVideoGeneratedList
          projectId={projectId}
          onVideoClick={handleVideoClick}
          playerRef={playerRef}
        />
      )}

      {/* 내 스페이스 에셋 선택 모달 */}
      {(variant === 'full' || variant === 'generate-only') &&
        spaceData?.data && (
          <AssetPickerModal
            isOpen={isPickerOpen}
            spaceId={spaceData.data.id}
            rootFolderId={spaceData.data.rootFolderId}
            contentTypes={[DtoContentType.image]}
            maxSelections={1}
            onClose={() => setIsPickerOpen(false)}
            onSelect={handleAssetSelect}
          />
        )}
    </div>
  );
}
